import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil, switchMap, filter } from 'rxjs/operators';
import { HospitalAccessService } from '../../../../services/hospital-access.service';
import { ActiveHospitalService, HospitalSite } from '../../../../services/active-hospital.service';

export interface HospitalSiteSelectorData {
  sites: HospitalSite[];
  mode?: 'select' | 'add'; // initial mode
}

@Component({
  selector: 'app-hospital-site-selector',
  templateUrl: './hospital-site-selector.component.html',
  styleUrls: ['./hospital-site-selector.component.scss'],
  standalone: false
})
export class HospitalSiteSelectorComponent implements OnInit, OnDestroy {

  /** Current view mode */
  mode: 'select' | 'add' = 'select';

  /** Granted sites available for selection */
  grantedSites: HospitalSite[] = [];

  /** Currently highlighted site in the list */
  selectedCode: string = '';

  /** Search control for add-new mode */
  searchControl = new FormControl('');

  /** Search results */
  searchResults: any[] = [];

  /** Selected hospital from search */
  selectedNewHospital: any = null;

  /** Loading state */
  isSearching = false;
  isSubmitting = false;

  /** Confirmation message after request */
  confirmationMessage = '';

  private destroy$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<HospitalSiteSelectorComponent>,
    @Inject(MAT_DIALOG_DATA) public data: HospitalSiteSelectorData,
    private hospitalAccessService: HospitalAccessService,
    private activeHospitalService: ActiveHospitalService
  ) {}

  ngOnInit(): void {
    this.grantedSites = this.data.sites.filter(s => s.status === 'granted');
    this.selectedCode = this.activeHospitalService.getSelectedHospitalCode();
    this.mode = this.data.mode || 'select';

    // Setup search typeahead
    this.searchControl.valueChanges.pipe(
      takeUntil(this.destroy$),
      debounceTime(300),
      filter((val): val is string => typeof val === 'string' && val.trim().length >= 2),
      switchMap(query => {
        this.isSearching = true;
        this.selectedNewHospital = null;
        return this.hospitalAccessService.searchHospitals(query);
      })
    ).subscribe({
      next: (res: any) => {
        this.isSearching = false;
        this.searchResults = res?.data || [];
      },
      error: () => {
        this.isSearching = false;
        this.searchResults = [];
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Select an existing site from the list */
  selectSite(site: HospitalSite): void {
    this.selectedCode = site.hospitalCode;
  }

  /** Confirm selection of an existing site */
  confirmExistingSite(): void {
    if (!this.selectedCode) return;
    const success = this.activeHospitalService.switchSite(this.selectedCode);
    if (success) {
      this.dialogRef.close({ action: 'switched', hospitalCode: this.selectedCode });
    }
  }

  /** Switch to add-new mode */
  showAddNew(): void {
    this.mode = 'add';
    this.searchControl.setValue('');
    this.searchResults = [];
    this.selectedNewHospital = null;
    this.confirmationMessage = '';
  }

  /** Return to site selection mode */
  backToExisting(): void {
    this.mode = 'select';
    this.selectedNewHospital = null;
    this.confirmationMessage = '';
  }

  /** Select a hospital from search results */
  selectSearchResult(hospital: any): void {
    this.selectedNewHospital = hospital;
    this.searchControl.setValue(`${hospital.practiceName} (${hospital.hospitalCode})`, { emitEvent: false });
    this.searchResults = [];
  }

  /** Submit the access request for the selected new hospital */
  confirmNewSite(): void {
    if (!this.selectedNewHospital || this.isSubmitting) return;

    this.isSubmitting = true;
    this.hospitalAccessService.submitRequest(this.selectedNewHospital.hospitalCode).subscribe({
      next: (res: any) => {
        this.isSubmitting = false;
        this.confirmationMessage = res?.data?.message || 'Request submitted successfully.';

        // Add to active hospital service as pending
        this.activeHospitalService.addSite({
          hospitalCode: this.selectedNewHospital.hospitalCode,
          practiceName: this.selectedNewHospital.practiceName,
          isPrimary: false,
          status: 'pending',
          requestId: res?.data?.requestId
        });
      },
      error: (err: any) => {
        this.isSubmitting = false;
        this.confirmationMessage = err?.error?.data || 'Error submitting request. Please try again.';
      }
    });
  }

  /** Close dialog */
  close(): void {
    this.dialogRef.close(null);
  }
}
